import { CustomError } from '@core/utils';
import { useQuery } from '@tanstack/react-query';

import { AuthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';

const LOCATION_CONSENT_DECLINED_KEY = 'denied-location-drawer';

/**
 * 위치기반서비스 동의의 단일 진실 출처(single source of truth).
 *
 * - `isConsented`: 동의 상태. 비로그인은 동의 절차가 없어 항상 true.
 *                  `undefined`는 로딩 중을 의미.
 * - `ensureConsent`: 동의 필요 시 drawer를 띄우고 응답까지 대기 후 결과 반환.
 *                    이미 동의된 상태면 즉시 true,
 *                    이번 세션에 거부 이력이 있으면 즉시 false.
 */
export const useLocationConsent = () => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const setDrawer = useGlobalStore(
    ({ setHandleLocationServiceConsentDrawer }) =>
      setHandleLocationServiceConsentDrawer,
  );

  const consentQuery = useQuery({
    ...AuthQueryOptions[
      'get:/wello/v2/auth/member-wello/location-service-consent'
    ](),
    enabled: !!isLogin,
    select: (data) => data.context?.locationServiceConsentYN ?? false,
  });

  const isConsented: boolean | undefined =
    isLogin === undefined
      ? undefined
      : isLogin === false
        ? true
        : consentQuery.data;

  const ensureConsent = async (): Promise<boolean> => {
    if (isConsented === undefined)
      throw new CustomError({
        return_message: '위치 기반 서비스 동의 정보가 없습니다.',
      });

    if (isConsented === true) return true;

    if (sessionStorage.getItem(LOCATION_CONSENT_DECLINED_KEY) === 'true')
      return false;

    return new Promise<boolean>((resolve) => {
      setDrawer(async (isAgree) => {
        if (!isAgree)
          sessionStorage.setItem(LOCATION_CONSENT_DECLINED_KEY, 'true');
        resolve(isAgree);
      });
    });
  };

  return { isConsented, ensureConsent };
};
